import { VENDOR, type VendorCode } from 'utils/vendorDisplayName'

/**
 * Dialog context model for TaskSettingsModal.
 *
 * Design — three orthogonal input blocks:
 *   - target      — what the dialog does (new / edit whole task / edit single occurrence).
 *   - preselected — what is already chosen and should be READ-ONLY in the form.
 *   - pickerScope — constraints on what is ALLOWED inside pickers (intersection semantics).
 *
 * `fieldPolicy` is DERIVED from the above and is not part of the input contract.
 *
 * Raw payload objects (task/mission/scenario/exec-task instances) live in a separate
 * `preselectedData` prop of TaskSettingsModal — this module only deals with semantics.
 */

export type CleaningItemType = 'mission' | 'scenario' | 'executable_task'

export interface CleaningItemRef {
  type: CleaningItemType
  id: string | number
}

export type DialogTarget =
  | { kind: 'new' }
  | { kind: 'task_all'; taskId: number }
  | { kind: 'task_occurrence'; taskId: number }

export interface Preselected {
  robot?: { id: number }
  cleaningItem?: CleaningItemRef
}

export interface PickerScope {
  robotIds?: number[]
  vendor?: VendorCode
  allowedCleaningItemTypes?: CleaningItemType[]
  allowedCleaningItems?: CleaningItemRef[]
}

export interface DialogContext {
  target: DialogTarget
  preselected: Preselected
  pickerScope: PickerScope
}

/** Narrow, pragmatic policy. Extend by need, not upfront. */
export interface FieldPolicy {
  canChangeRobot: boolean
  canChangeCleaningItem: boolean
  canEditTaskName: boolean
  canEditSchedule: boolean
  resetMode: 'none' | 'full'
  showReset: boolean
}

/** True when candidate cleaning-item type is allowed by picker scope (or scope is open). */
export function intersectAllowedTypes(scope: PickerScope | undefined, candidate: CleaningItemType): boolean {
  if (!scope) return true
  const allowed = scope.allowedCleaningItemTypes
  if (!allowed || allowed.length === 0) return true
  return allowed.includes(candidate)
}

/** Derive field policy from context and current selection. */
export function derivePolicy(args: {
  context: DialogContext
  effectiveVendor: VendorCode | undefined
  hasAnySelection: boolean
}): FieldPolicy {
  const { context, effectiveVendor, hasAnySelection } = args
  const { preselected, target } = context

  const canChangeRobot = !preselected.robot
  const canChangeCleaningItem = !preselected.cleaningItem

  const isGausiumExecTaskSelection =
    preselected.cleaningItem?.type === 'executable_task' || effectiveVendor === VENDOR.GAUSIUM

  const canEditTaskName = !isGausiumExecTaskSelection && !preselected.cleaningItem

  const canEditSchedule = target.kind !== 'task_occurrence' && effectiveVendor !== VENDOR.GAUSIUM

  const resetMode: FieldPolicy['resetMode'] = (() => {
    if (preselected.cleaningItem || preselected.robot) return 'none'
    return 'full'
  })()

  const showReset = resetMode !== 'none'

  return {
    canChangeRobot,
    canChangeCleaningItem,
    canEditTaskName,
    canEditSchedule,
    resetMode,
    showReset,
  }
}
